import { View, StyleSheet, ImageStyle } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import useResponsiveScreen from "../../hooks/useResponsiveScreen";
import { Books } from "@phosphor-icons/react/dist/csr/Books";
import { Palette } from "lucide-react";
import { Calculator } from "@phosphor-icons/react/dist/csr/Calculator";
import { Question } from "@phosphor-icons/react/dist/csr/Question";
import { FileText } from "@phosphor-icons/react/dist/csr/FileText";
import { Image as ImageIcon } from "@phosphor-icons/react/dist/csr/Image";
import ImageUpload from "../../components/common/general/ImageUpload/ImageUpload";
import { StoragePathType } from "@axio/graphql/generated/graphql";

interface FormData {
  logo: string;
  favicon: string;
  primaryColor: string;
  financialInformationUrl: string;
  helpCenterUrl: string;
  schoolResourcesUrl: string;
  officialTranscriptsUrl: string;
}

interface AdminBrandingSettingsContainerProps {
  formData: FormData;
  updateFormData: (field: string, value: string) => void;
  handleLogoAfterUpload: (logoUrl: string) => void;
  handleFaviconUpload: () => void;
  handleColorChange: (color: string) => void;
  validateHexColor: (color: string) => boolean;
  getFaviconPreviewUrl: () => string;
  isSubmitting: boolean;
  tenantPrimaryColor?: string;
  tenantFavicon180Url?: string;
}

function AdminBrandingSettingsContainer({
  formData,
  updateFormData,
  handleLogoAfterUpload,
  handleFaviconUpload,
  handleColorChange,
  validateHexColor,
  getFaviconPreviewUrl,
  isSubmitting,
  tenantPrimaryColor,
  tenantFavicon180Url,
}: AdminBrandingSettingsContainerProps) {
  const { isLarge } = useResponsiveScreen();

  return (
    <View style={[styles.mainLayout, !isLarge && styles.mainLayoutMobile]}>
      <View style={[styles.leftColumn, !isLarge && styles.leftColumnMobile]}>
        <View style={!isLarge ? [styles.sectionCard, styles.mobileSectionCard] : styles.leftSectionCard}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionIconContainer}>
              <View style={[styles.sectionIcon, { backgroundColor: "#FCE7F3" }]}>
                <Palette size={20} color="#DB2777" />
              </View>
            </View>
            <View style={styles.sectionHeaderText}>
              <CustomText
                text="Branding & Customization"
                size="xxl"
                weight="bold"
                useTranslationText={false}
                style={{ color: "#111827" }}
              />
              <CustomText
                text="Customize your institution's visual identity and brand elements."
                size="s"
                useTranslationText={false}
                style={{ color: "#475467" }}
              />
            </View>
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View>
            <CustomText
              text="Institution Logo"
              size="m"
              weight="bold"
              useTranslationText={false}
              style={{ color: "#344054", marginBottom: 8 }}
            />
            <ImageUpload
              value={formData.logo}
              onChange={handleLogoAfterUpload}
              storagePath={StoragePathType.TenantLogo}
              imageStyle={styles.logoImageStyle as ImageStyle}
              placeholderContent={<ImageIcon size={24} color="#9CA3AF" />}
            />
          </View>

          <CustomSpacing type="vertical" size="xl" />

          <View>
            <CustomText
              text="Color Theme"
              size="m"
              weight="bold"
              useTranslationText={false}
              style={{ color: "#344054", marginBottom: 16 }}
            />

            <View>
              <CustomText
                text="Primary Color (Buttons & Links)"
                size="s"
                weight="medium"
                useTranslationText={false}
                style={{ color: "#344054", marginBottom: 8 }}
              />

              <View style={styles.colorInputContainer}>
                <View
                  style={[
                    styles.colorPreview,
                    {
                      backgroundColor: validateHexColor(formData.primaryColor) ? formData.primaryColor : "#D1D5DB",
                    },
                  ]}
                />
                <CustomTextInput
                  value={formData.primaryColor}
                  onChangeText={handleColorChange}
                  placeholder={tenantPrimaryColor || "#434756"}
                  style={styles.colorInput}
                />
              </View>

              {!validateHexColor(formData.primaryColor) && formData.primaryColor && (
                <CustomText
                  text="Please enter a valid hex color code"
                  size="xs"
                  useTranslationText={false}
                  style={{ color: "#EF4444", marginTop: 4 }}
                />
              )}
            </View>

            <CustomSpacing type="vertical" size="m" />

            <View>
              <CustomText
                text="Preview"
                size="s"
                weight="medium"
                useTranslationText={false}
                style={{ color: "#344054", marginBottom: 8 }}
              />
              <View style={styles.previewContainer}>
                <CustomButton
                  text="Sample Button"
                  styleType="primary"
                  size="m"
                  onPress={() => {}}
                  bold={true}
                  useTranslationText={false}
                  style={{
                    backgroundColor: validateHexColor(formData.primaryColor) ? formData.primaryColor : "#3E68FE",
                    maxWidth: "140px",
                  }}
                  textStyle={{ fontSize: 12 }}
                />
                <CustomText
                  text="Button text uses the selected text color on the primary background."
                  size="xs"
                  useTranslationText={false}
                  style={{ color: "#667085" }}
                />
              </View>
            </View>
          </View>

          <CustomSpacing type="vertical" size="xl" />

          <View>
            <CustomText
              text="Favicon"
              size="m"
              weight="bold"
              useTranslationText={false}
              style={{ color: "#344054", marginBottom: 8 }}
            />

            <div
              style={{
                width: "100%",
                minHeight: 120,
                maxHeight: 200,
                borderRadius: 8,
                borderWidth: 2,
                borderStyle: "dashed",
                borderColor: "#D1D5DB",
                backgroundColor: "#F9FAFB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                padding: 16,
                boxSizing: "border-box",
              }}
              onClick={handleFaviconUpload}>
              {formData.favicon || tenantFavicon180Url ? (
                <img
                  src={getFaviconPreviewUrl()}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: 6,
                  }}
                  alt="Favicon"
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    textAlign: "center",
                  }}>
                  <ImageIcon size={24} color="#9CA3AF" />
                  <CustomText
                    text={isSubmitting ? "Uploading..." : "Click to upload favicon"}
                    size="m"
                    style={{ color: "#344054", fontWeight: "500" }}
                    useTranslationText={false}
                  />
                  <CustomText
                    text="PNG, ICO, SVG, JPG up to 1MB"
                    size="s"
                    style={{ color: "#9CA3AF" }}
                    useTranslationText={false}
                  />
                  <CustomText
                    text="Square ratio (1:1) required, 16×16 to 1024×1024px"
                    size="xs"
                    style={{ color: "#9CA3AF" }}
                    useTranslationText={false}
                  />
                </div>
              )}
            </div>
          </View>
        </View>
      </View>

      <View style={[styles.rightColumn, !isLarge && styles.rightColumnMobile]}>
        <View
          style={
            !isLarge ? [styles.sectionCard, styles.mobileSectionCard, styles.resourcesCard] : styles.rightSectionCard
          }>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionIconContainer}>
              <View style={[styles.sectionIcon, { backgroundColor: "#D1F2F6" }]}>
                <Books size={24} color="#157984" />
              </View>
            </View>
            <View style={styles.sectionHeaderText}>
              <CustomText
                text="Student Resources Panel"
                size="l"
                weight="bold"
                useTranslationText={false}
                style={{ color: "#101828" }}
              />
              <CustomText
                text="Manage the resources shown in the student slide-out panel."
                size="s"
                weight="medium"
                useTranslationText={false}
                style={{ color: "#667085" }}
              />
            </View>
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.resourceFields}>
            <View style={styles.resourceField}>
              <View style={styles.resourceIconText}>
                <View style={styles.resourceIcon}>
                  <Calculator size={20} color="#16A34A" />
                </View>
                <CustomText
                  text="Financial Information"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#101828" }}
                />
              </View>
              <CustomTextInput
                value={formData.financialInformationUrl}
                onChangeText={(value) => updateFormData("financialInformationUrl", value)}
                placeholder="https://university.edu/financial-aid"
                style={styles.urlInput}
              />
              <CustomText
                text="Link to tuition, payments, and financial aid information"
                size="xs"
                useTranslationText={false}
                style={{ color: "#667085" }}
              />
            </View>

            <View style={styles.resourceField}>
              <View style={styles.resourceIconText}>
                <View style={styles.resourceIcon}>
                  <Question size={20} color="#101828" />
                </View>
                <CustomText
                  text="Help Center"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#101828" }}
                />
              </View>
              <CustomTextInput
                value={formData.helpCenterUrl}
                onChangeText={(value) => updateFormData("helpCenterUrl", value)}
                placeholder="https://help.university.edu"
                style={styles.urlInput}
              />
              <CustomText
                text="FAQ and documentation for students"
                size="xs"
                useTranslationText={false}
                style={{ color: "#667085" }}
              />
            </View>

            <View style={styles.resourceField}>
              <View style={styles.resourceIconText}>
                <View style={styles.resourceIcon}>
                  <Books size={20} color="#7B3395" />
                </View>
                <CustomText
                  text="School Resources"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#101828" }}
                />
              </View>
              <CustomTextInput
                value={formData.schoolResourcesUrl}
                onChangeText={(value) => updateFormData("schoolResourcesUrl", value)}
                placeholder="https://resources.university.edu"
                style={styles.urlInput}
              />
              <CustomText
                text="Library, downloads, and institutional resources"
                size="xs"
                useTranslationText={false}
                style={{ color: "#667085" }}
              />
            </View>

            <View style={styles.resourceField}>
              <View style={styles.resourceIconText}>
                <View style={styles.resourceIcon}>
                  <FileText size={20} color="#101828" />
                </View>
                <CustomText
                  text="Official Transcripts"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#101828" }}
                />
              </View>
              <CustomTextInput
                value={formData.officialTranscriptsUrl}
                onChangeText={(value) => updateFormData("officialTranscriptsUrl", value)}
                placeholder="https://transcripts.university.edu"
                style={styles.urlInput}
              />
              <CustomText
                text="Request or download official transcripts"
                size="xs"
                useTranslationText={false}
                style={{ color: "#667085" }}
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  mainLayout: {
    flexDirection: "row",
    gap: 0,
    alignItems: "stretch",
  },
  mainLayoutMobile: {
    flexDirection: "column",
    gap: 24,
  },
  leftColumn: {
    flex: 2,
  },
  leftColumnMobile: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  rightColumnMobile: {
    flex: 1,
  },
  sectionCard: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    height: "100%",
    padding: 24,
  },
  leftSectionCard: {
    backgroundColor: "#FFFFFF",
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderWidth: 1,
    borderRightWidth: 0,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    height: "100%",
    padding: 24,
  },
  rightSectionCard: {
    backgroundColor: "#FCFCFD",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    height: "100%",
    padding: 24,
  },
  mobileSectionCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    padding: 24,
  },
  resourcesCard: {
    backgroundColor: "#FCFCFD",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  sectionIconContainer: {
    marginTop: 4,
  },
  sectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  sectionHeaderText: {
    flex: 1,
    gap: 4,
  },
  colorInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  colorPreview: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  colorInput: {
    flex: 1,
    maxWidth: 200,
  },
  previewContainer: {
    backgroundColor: "#F9FAFB",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    gap: 8,
  },
  resourceFields: {
    gap: 24,
  },
  resourceField: {
    gap: 8,
  },
  resourceIconText: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  resourceIcon: {
    width: 24,
    height: 24,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  urlInput: {
    width: "100%",
  },
  logoImageStyle: {
    // @ts-expect-error - works for web
    width: "auto !important",
    minHeight: 120,
    maxHeight: 200,
    objectFit: "contain",
    borderRadius: 6,
  },
});

export default AdminBrandingSettingsContainer;
